import VChart from '@visactor/vchart';
import { DEFAULT_ANIMATIONS } from '../config/animations';
import { DEFAULT_TOOLTIP } from '../config/tooltips';
import { DEFAULT_LEGEND } from '../config/legends';
import { LIGHT_THEME } from '../config/themes';

/**
 * Base chart class that provides common functionality for all chart types
 */
class BaseChart {
  constructor(data, options = {}, container) {
    this.data = data;
    this.options = options;
    this.container = container;
    this.chart = null;
    this.theme = options.theme || LIGHT_THEME;
  }

  /**
   * Resolve the name (category) field from options and data.
   * Agents may pass nameField, categoryField, seriesField, or xField.
   * Falls back to auto-detecting the first string-valued field in the data.
   * @returns {string} Resolved name field key
   */
  _resolveNameField() {
    for (const key of ['nameField', 'categoryField', 'seriesField', 'xField']) {
      if (this.options[key]) return this.options[key];
    }

    const valueField = this.options.valueField || 'value';
    if (Array.isArray(this.data) && this.data.length > 0) {
      const sample = this.data[0];
      for (const key of Object.keys(sample)) {
        if (key === valueField || key.startsWith('_')) continue;
        if (typeof sample[key] === 'string') return key;
      }
    }

    return 'name';
  }

  /**
   * Resolve the value (numeric) field from options and data.
   * Agents may pass valueField or yField.
   * Falls back to auto-detecting the first numeric field in the data,
   * skipping ordinal fields like rank, index, id.
   * @returns {string} Resolved value field key
   */
  _resolveValueField() {
    if (this.options.valueField) return this.options.valueField;
    if (this.options.yField) return this.options.yField;

    if (Array.isArray(this.data) && this.data.length > 0) {
      const sample = this.data[0];
      // First check for a field literally called 'value'
      if ('value' in sample) return 'value';
      const ordinals = new Set(['rank', 'index', 'id', 'row_number']);
      for (const key of Object.keys(sample)) {
        if (ordinals.has(key) || key.startsWith('_')) continue;
        if (typeof sample[key] === 'number') return key;
      }
    }

    return 'value';
  }

  /**
   * Resolve a data field name from options, trying alternatives when the
   * default is not present in the data.
   * Returns null when data exists but neither the default nor any alternative
   * is found, allowing callers to chain with type-based resolvers.
   * @param {string} optionKey The options property name to check first
   * @param {string} defaultName Default field name if nothing else matches
   * @param {string[]} alternatives Alternative field names to try in order
   * @returns {string|null} Resolved field name, or null if not found in data
   */
  _resolveField(optionKey, defaultName, alternatives) {
    if (this.options[optionKey]) return this.options[optionKey];

    if (Array.isArray(this.data) && this.data.length > 0) {
      const sample = this.data[0];
      if (defaultName in sample) return defaultName;
      for (const alt of alternatives) {
        if (alt in sample) return alt;
      }
      // Data exists but no candidate matched — return null so callers
      // can fall through to type-based detection.
      return null;
    }

    return defaultName;
  }

  /**
   * Pivot wide-format data (multiple yField columns) into long format
   * for multi-series rendering.
   * @param {string} xField The x-axis field name
   * @param {string[]} yFields Array of y-axis field names to pivot
   * @returns {{ data: Array, yField: string, seriesField: string }}
   */
  _pivotWideToLong(xField, yFields) {
    const pivotedValues = [];
    for (const row of this.data) {
      for (const field of yFields) {
        pivotedValues.push({
          [xField]: row[xField],
          value: parseFloat(row[field]) || 0,
          series: field
        });
      }
    }
    return {
      data: [{ id: 'data', values: pivotedValues }],
      yField: 'value',
      seriesField: 'series',
    };
  }

  /**
   * Detect x-axis type from explicit option or data inspection.
   * @param {string} xField The x-axis field name
   * @returns {string} Axis type ('band', 'time', or 'linear')
   */
  _detectXAxisType(xField) {
    if (this.options.xAxisType === 'time') return 'time';
    if (this.options.xAxisType) return this.options.xAxisType;
    if (Array.isArray(this.data) && this.data.length > 0
        && typeof this.data[0][xField] === 'string') {
      return 'band';
    }
    return 'linear';
  }

  /**
   * Get common configuration that applies to all chart types
   * @returns {Object} Common chart configuration
   */
  getCommonConfig() {
    return {
      // Theme configuration
      theme: this.theme,
      
      // Tooltip configuration
      tooltip: {
        ...DEFAULT_TOOLTIP,
        ...this.options.tooltip
      },
      
      // Legend configuration
      legends: {
        ...DEFAULT_LEGEND,
        ...this.options.legends
      },
      
      // Animation configurations
      animationAppear: {
        ...DEFAULT_ANIMATIONS.appear,
        ...this.options.animationAppear
      },
      animationUpdate: {
        ...DEFAULT_ANIMATIONS.update,
        ...this.options.animationUpdate
      },
      
      // Interactive state configurations
      state: {
        hover: {
          enabled: true,
          ...this.options.hoverState
        },
        selected: {
          enabled: true,
          ...this.options.selectedState
        }
      }
    };
  }
  
  /**
   * Get a meaningful name for a data item to use in legends
   * @param {Object} item Data item
   * @param {number} index Item index
   * @returns {string} Meaningful name for legend
   */
  getMeaningfulItemName(item, index) {
    // Priority order for finding a meaningful name:
    // 1. Use name field if it exists
    // 2. Use label field if it exists
    // 3. Use title field if it exists
    // 4. Use displayName field if it exists
    // 5. Use key field if it exists
    // 6. Use id field if it exists
    // 7. Use xField value if configured
    // 8. Use first string property found in the item
    // 9. Fallback to item-index format

    // 1-6: Check for common name fields
    for (const field of ['name', 'label', 'title', 'displayName', 'key', 'id']) {
      if (item[field] && typeof item[field] === 'string') {
        return item[field];
      }
    }
    
    // 7: Use xField value if configured
    if (this.options.xField && item[this.options.xField]) {
      return item[this.options.xField].toString();
    }
    
    // 8: Use first string property
    for (const key in item) {
      if (typeof item[key] === 'string' && key !== '_itemId' && !key.startsWith('_')) {
        return item[key];
      }
    }
    
    // 9: Fallback to default format
    return `Item ${index + 1}`;
  }
  
  /**
   * Prepare data for VChart format
   * @returns {Array} Formatted data for VChart
   */
  prepareData() {
    // Default implementation, can be overridden by specific chart types
    if (!Array.isArray(this.data)) {
      this.data = [this.data];
    }
    
    // Add meaningful item naming
    const formattedData = this.data.map((item, index) => {
      const meaningfulName = this.getMeaningfulItemName(item, index);
      
      return {
        ...item,
        _itemId: meaningfulName, // Use meaningful name as the itemId (for legend)
        name: meaningfulName // Use meaningful name for legend
      };
    });
    
    return [
      {
        id: 'data',
        values: formattedData
      }
    ];
  }
  
  /**
   * Get chart specification - to be implemented by derived classes
   * @returns {Object} Chart specification
   */
  getChartSpecification() {
    throw new Error('getChartSpecification must be implemented by derived classes');
  }
  
  /**
   * Render the chart
   * @returns {Object} VChart instance
   */
  render() {
    const spec = this.getChartSpecification();
    
    if (this.chart) {
      this.chart.release();
    }
    
    this.chart = new VChart(spec, { dom: this.container });
    this.chart.renderSync();
    
    // Attach event handlers if provided
    if (this.options.events) {
      Object.entries(this.options.events).forEach(([event, handler]) => {
        this.chart.on(event, handler);
      });
    }
    
    return this.chart;
  }
  
  /**
   * Update chart with new data
   * @param {Array} newData New data to display
   */
  update(newData) {
    if (!this.chart) return;
    
    this.data = newData;
    const spec = this.getChartSpecification();
    
    this.chart.updateSpec(spec);
    this.chart.renderSync();
  }
  
  /**
   * Update chart options
   * @param {Object} newOptions New options to apply
   */
  updateOptions(newOptions) {
    if (!this.chart) return;
    
    this.options = { ...this.options, ...newOptions };
    const spec = this.getChartSpecification();
    
    this.chart.updateSpec(spec);
    this.chart.renderSync();
  }
  
  /**
   * Release chart resources
   */
  release() {
    if (this.chart) {
      this.chart.release();
      this.chart = null;
    }
  }
}

export default BaseChart; 